import React from 'react';
import {
  Reaction,
  abbreviateCount,
  bookmarkTrip,
  likeTrip,
  unbookmarkTrip,
  unlikeTrip,
} from '../../../services/engagement/api';
import './ReactionBar.css';

interface ReactionBarProps {
  tripId: string;
  reaction?: Reaction;
  /** Compact = icons only (cards); default shows the like count too. */
  compact?: boolean;
  className?: string;
}

const emptyReaction: Reaction = { liked: false, saved: false, likes: 0 };

/**
 * Heart (public like + count) + bookmark (private save) with optimistic toggles
 * (T2.14). Reverts on error. Counts are abbreviated + never promised exact.
 */
const ReactionBar: React.FC<ReactionBarProps> = ({ tripId, reaction, compact, className }) => {
  const [state, setState] = React.useState<Reaction>(reaction || emptyReaction);
  const [busy, setBusy] = React.useState<{ like?: boolean; save?: boolean }>({});

  React.useEffect(() => {
    if (reaction) setState(reaction);
  }, [reaction]);

  const toggleLike = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy.like) return;
    const next = !state.liked;
    setBusy((b) => ({ ...b, like: true }));
    setState((s) => ({ ...s, liked: next, likes: Math.max(0, s.likes + (next ? 1 : -1)) }));
    try {
      await (next ? likeTrip(tripId) : unlikeTrip(tripId));
    } catch (error) {
      setState((s) => ({ ...s, liked: !next, likes: Math.max(0, s.likes + (next ? -1 : 1)) }));
      console.error('Like toggle failed:', error);
    } finally {
      setBusy((b) => ({ ...b, like: false }));
    }
  };

  const toggleSave = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (busy.save) return;
    const next = !state.saved;
    setBusy((b) => ({ ...b, save: true }));
    setState((s) => ({ ...s, saved: next }));
    try {
      await (next ? bookmarkTrip(tripId) : unbookmarkTrip(tripId));
    } catch (error) {
      setState((s) => ({ ...s, saved: !next }));
      console.error('Bookmark toggle failed:', error);
    } finally {
      setBusy((b) => ({ ...b, save: false }));
    }
  };

  return (
    <div className={`v2-reaction-bar ${className || ''}`}>
      <button
        type="button"
        className={`v2-reaction-bar__btn ${state.liked ? 'v2-reaction-bar__btn--liked' : ''}`}
        onClick={toggleLike}
        disabled={busy.like}
        aria-pressed={state.liked}
        aria-label={state.liked ? 'Unlike' : 'Like'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M12 21s-8-5.2-8-10.6A4.4 4.4 0 0 1 12 6a4.4 4.4 0 0 1 8 4.4C20 15.8 12 21 12 21z"
            fill={state.liked ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
        {!compact && <span>{abbreviateCount(state.likes)}</span>}
      </button>

      <button
        type="button"
        className={`v2-reaction-bar__btn ${state.saved ? 'v2-reaction-bar__btn--saved' : ''}`}
        onClick={toggleSave}
        disabled={busy.save}
        aria-pressed={state.saved}
        aria-label={state.saved ? 'Remove bookmark' : 'Bookmark'}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
          <path
            d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"
            fill={state.saved ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </div>
  );
};

export default ReactionBar;
